import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Stack, Button, Typography } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import PropTypes from "prop-types";
import { buttonStyles } from "services/utils";
import PeriodListView from "./components/PeriodListView";
import PeriodDashboardView from "./components/PeriodDashboardView";
import InputGridView from "./components/InputGridView";
import RunDetailView from "./components/RunDetailView";

const SUBVIEW_DASHBOARD = 0;
const SUBVIEW_INPUT_GRID = 1;
const SUBVIEW_RUNS = 2;

const PeriodsRunsView = ({ canEdit }) => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [activeSubView, setActiveSubView] = useState(SUBVIEW_DASHBOARD);

  // Dashboard/Input Grid/Runs only ever make sense for a chosen period, so
  // until one is picked there's nothing to show but the period list itself -
  // no nav bar, no empty tabs to click into.
  const handleSelectPeriod = (period) => {
    setSelectedPeriod(period);
    setSelectedRun(null);
    setActiveSubView(SUBVIEW_DASHBOARD);
  };

  const handleBackToPeriods = () => {
    setSelectedPeriod(null);
    setSelectedRun(null);
  };

  const handleSelectRun = (run) => {
    setSelectedRun(run);
    setActiveSubView(SUBVIEW_RUNS);
  };

  if (!selectedPeriod) {
    return <PeriodListView canEdit={canEdit} onSelectPeriod={handleSelectPeriod} />;
  }

  const buttons = [
    { label: t("admin.payroll.periodsAndRuns.tabs.dashboard"), viewIndex: SUBVIEW_DASHBOARD },
    { label: t("admin.payroll.periodsAndRuns.tabs.inputGrid"), viewIndex: SUBVIEW_INPUT_GRID },
    // { label: t("admin.payroll.periodsAndRuns.tabs.runs"), viewIndex: SUBVIEW_RUNS },
  ];

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToPeriods}
        color="secondary"
        size="small"
        sx={{ mb: 1 }}
      >
        {t("admin.payroll.periodsAndRuns.backToPeriods")}
      </Button>

      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {selectedPeriod.name}
      </Typography>

      <Stack pb={2}>
        <Grid container spacing={1} sx={{ justifyContent: { xs: "center", md: "flex-start" } }}>
          {buttons.map(({ label, viewIndex }) => (
            <Grid item xs={12} sm={4} md="auto" key={viewIndex}>
              <Button
                sx={{
                  ...buttonStyles(activeSubView === viewIndex),
                  width: { xs: "100%", md: "auto" },
                }}
                variant={activeSubView === viewIndex ? "contained" : "outlined"}
                onClick={() => setActiveSubView(viewIndex)}
                color="primary"
              >
                {label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Stack>

      <Box>
        {activeSubView === SUBVIEW_DASHBOARD && (
          <PeriodDashboardView
            canEdit={canEdit}
            selectedPeriod={selectedPeriod}
            onPeriodUpdated={setSelectedPeriod}
            onSelectRun={handleSelectRun}
            onGoToInputGrid={() => setActiveSubView(SUBVIEW_INPUT_GRID)}
          />
        )}
        {activeSubView === SUBVIEW_INPUT_GRID && (
          <InputGridView canEdit={canEdit} selectedPeriod={selectedPeriod} />
        )}
        {activeSubView === SUBVIEW_RUNS && (
          <RunDetailView
            canEdit={canEdit}
            selectedPeriod={selectedPeriod}
            selectedRun={selectedRun}
            onRunUpdated={setSelectedRun}
          />
        )}
      </Box>
    </Box>
  );
};

PeriodsRunsView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default PeriodsRunsView;
